import type React from 'react';
import { useCallback, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { InputBase } from '@/lib/base/forms/input/input';
import { InputGroup, InputPrefix } from '@/lib/base/forms/input/input-group';

const DURATION_QUICK_PICKS = [15, 30, 45, 60, 90, 120] as const;
const DEFAULT_CUSTOM_DURATION = 75;

function isQuickPick(duration: number): boolean {
  return (DURATION_QUICK_PICKS as readonly number[]).includes(duration);
}

interface DurationButtonProps {
  duration: number;
  current: number;
  disabled: boolean;
  onSelect: (d: number) => void;
}

// Per FRONTEND_PATTERNS.md "Never Roll Your Own (MANDATORY)": buttons must come
// from the base Button component. Toggle state is expressed via the `color`
// prop (`primary` selected / `secondary` unselected); `aria-pressed` is
// forwarded through Button's spread of HTML button attributes so screen
// readers still report the toggle state.
//
// Originally fixed in audit-2026-04-30 Finding 2; the main-merge in commit
// 85154f5 reverted it by accepting upstream's raw-<button> implementation
// (which also added a new CustomPill that bypasses the design system).
const DurationButton: React.FC<DurationButtonProps> = ({
  duration,
  current,
  disabled,
  onSelect,
}) => {
  const handleClick = useCallback(
    () => onSelect(duration),
    [duration, onSelect]
  );
  const isSelected = current === duration;
  return (
    <Button
      size='sm'
      color={isSelected ? 'primary' : 'secondary'}
      isDisabled={disabled}
      onClick={handleClick}
      aria-pressed={isSelected}
    >
      {duration} min
    </Button>
  );
};

interface CustomPillProps {
  active: boolean;
  disabled: boolean;
  onSelect: () => void;
}

const CustomPill: React.FC<CustomPillProps> = ({
  active,
  disabled,
  onSelect,
}) => {
  return (
    <Button
      size='sm'
      color={active ? 'primary' : 'secondary'}
      isDisabled={disabled}
      onClick={onSelect}
      aria-pressed={active}
    >
      Custom
    </Button>
  );
};

interface DurationFieldProps {
  value: unknown;
  onChange: (next: unknown) => void;
  disabled: boolean;
}

export const DurationField: React.FC<DurationFieldProps> = ({
  value,
  onChange,
  disabled,
}) => {
  const num = Number(value);
  const current = Number.isFinite(num) ? num : 30;
  const valueIsCustom = !isQuickPick(current);
  // Track whether the user explicitly opened "custom" mode. Without this,
  // typing a quick-pick value (e.g. 60) into the custom input would snap the
  // input shut, which is jarring while editing.
  const [manualMode, setManualMode] = useState(valueIsCustom);
  const showCustom = manualMode || valueIsCustom;

  const handleSelectQuickPick = useCallback(
    (d: number) => {
      setManualMode(false);
      onChange(d);
    },
    [onChange]
  );
  const handleSelectCustom = useCallback(() => {
    setManualMode(true);
    if (isQuickPick(current)) {
      onChange(DEFAULT_CUSTOM_DURATION);
    }
  }, [current, onChange]);
  const handleInputChange = useCallback(
    (next: string) => {
      const parsed = Math.round(Number(next));
      onChange(Number.isFinite(parsed) ? parsed : 0);
    },
    [onChange]
  );

  return (
    <div>
      <div className='flex flex-wrap gap-2'>
        {DURATION_QUICK_PICKS.map(d => (
          <DurationButton
            key={d}
            duration={d}
            current={showCustom ? -1 : current}
            disabled={disabled}
            onSelect={handleSelectQuickPick}
          />
        ))}
        <CustomPill
          active={showCustom}
          disabled={disabled}
          onSelect={handleSelectCustom}
        />
      </div>
      {showCustom ? (
        <div className='mt-3 max-w-[12rem]'>
          <InputGroup
            aria-label='Custom duration in minutes'
            value={String(current)}
            onChange={handleInputChange}
            isDisabled={disabled}
            trailingAddon={<InputPrefix isDisabled={disabled}>min</InputPrefix>}
          >
            <InputBase type='number' />
          </InputGroup>
        </div>
      ) : null}
    </div>
  );
};
